import React, { useState } from 'react';

import Security from '../../media/images/modules/security.jpg';
import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Kpis, Panel, Segments, Empty, useFlash, readForm } from '../../ui/kit.js';
import { day, clock, nextId } from '../../state/building.js';

const STATUSES = ['Open', 'Under review', 'Closed'];
const STATUS_TONE = { 'Open': 'alert', 'Under review': 'warn', 'Closed': 'ok' };
const SEVERITY_TONE = { 'Low': 'muted', 'Medium': 'warn', 'High': 'alert' };
const TYPES = ['Injury / slip and fall', 'Noise complaint', 'Security / access', 'Water leak', 'Vehicle damage', 'Property damage', 'Other'];

const SEED = [
    { id: 1, type: 'Injury / slip and fall', title: 'Guest slipped on wet pool deck', location: 'Pool deck, east stairs', severity: 'High', at: `${day(0)}, 11:20 AM`, by: 'Carla P. (pool attendant)', status: 'Open', responders: true, detail: 'Minor knee abrasion. First aid given, Fire Rescue declined transport.' },
    { id: 2, type: 'Noise complaint', title: 'Loud music after quiet hours', location: 'Unit 1603', severity: 'Low', at: `${day(0)}, 12:45 AM`, by: 'Unit 1503 resident', status: 'Under review', responders: false, detail: 'Concierge called unit twice. Second complaint this month.' },
    { id: 3, type: 'Security / access', title: 'Tailgating attempt at garage gate', location: 'Garage entrance, P1', severity: 'Medium', at: `${day(-1)}, 10:10 PM`, by: 'Marcus T. (security)', status: 'Under review', responders: false, detail: 'Unknown sedan followed a resident in. Plate captured on camera 4.' },
    { id: 4, type: 'Water leak', title: 'Ceiling leak from unit above', location: 'Unit 907 bathroom', severity: 'Medium', at: `${day(-1)}, 4:35 PM`, by: 'Robert Klein, 907', status: 'Closed', responders: false, detail: 'Supply line in 1007 replaced. Insurance notified.' },
    { id: 5, type: 'Vehicle damage', title: 'Fender bender at P2 ramp', location: 'Garage P2 ramp', severity: 'Low', at: `${day(-2)}, 8:05 AM`, by: 'Luis M. (maintenance)', status: 'Closed', responders: false, detail: 'Two resident vehicles, no injuries. Owners exchanged insurance.' }
];

/* Incident Reporting: one log for every slip, complaint and security
   event, each moved from open to closed with a record of who reported it. */
function IncidentsScreen() {
    const [incidents, setIncidents] = useState(SEED);
    const [filter, setFilter] = useState('All');
    const [severity, setSeverity] = useState('Medium');
    const [flash, notify] = useFlash();

    const shown = filter === 'All' ? incidents : incidents.filter(i => i.status === filter);

    const advance = incident => {
        const next = STATUSES[STATUSES.indexOf(incident.status) + 1];
        if (!next) return;
        setIncidents(list => list.map(i => (i.id === incident.id ? { ...i, status: next } : i)));
        notify(`${incident.title} moved to ${next}.${next === 'Closed' ? ' Report filed with management.' : ''}`);
    };

    const submit = event => {
        const values = readForm(event, ['title', 'location']);
        if (!values) return notify('Add a short summary and where it happened.');

        setIncidents(list => [{
            id: nextId(),
            type: values.type,
            title: values.title,
            location: values.location,
            severity,
            at: `${day(0)}, ${clock()}`,
            by: 'Front desk, you',
            status: 'Open',
            responders: Boolean(values.responders),
            detail: values.detail
        }, ...list]);
        setSeverity('Medium');
        setFilter('All');
        notify(`Incident logged.${severity === 'High' ? ' The property manager has been paged.' : ''}`);
    };

    const count = status => incidents.filter(i => i.status === status).length;

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Incident reporting" title="Incidents log" />

            <div className="banner">
                <img src={Security} alt="" />
                <p className="banner__caption">Every incident logged, followed up, and closed out.</p>
            </div>

            <Kpis items={[
                { label: 'Open', value: count('Open'), tone: count('Open') ? 'alert' : 'ok' },
                { label: 'Under review', value: count('Under review'), tone: 'warn' },
                { label: 'Closed', value: count('Closed'), tone: 'ok' },
                { label: 'Police / EMS called', value: incidents.filter(i => i.responders).length }
            ]} />

            {flash}

            <div className="split">
                <Panel as="form" className="panel--accent" onSubmit={submit}>
                    <h4>Report an incident</h4>
                    <div className="form">
                        <Field label="Type" wide>
                            <select name="type" className="form-control" defaultValue={TYPES[0]}>
                                {TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Summary" wide>
                            <input name="title" className="form-control" placeholder="e.g. Resident fell in the fitness center" />
                        </Field>
                        <Field label="Location" wide>
                            <input name="location" className="form-control" placeholder="e.g. Lobby, valet stand" />
                        </Field>
                        <div className="field field--wide">
                            <span className="form-label">Severity</span>
                            <Segments label="Severity" options={['Low', 'Medium', 'High']} value={severity} onChange={setSeverity} />
                        </div>
                        <Field label="What happened" wide>
                            <textarea name="detail" className="form-control" rows="3" placeholder="Who was involved, what was done, any witnesses" />
                        </Field>
                        <label className="field field--wide check">
                            <input type="checkbox" name="responders" style={{ width: 'auto' }} />
                            <span>Police or EMS were called</span>
                        </label>
                        <button type="submit" className="btn btn-primary">Log incident</button>
                    </div>
                </Panel>

                <div className="stack">
                    <Segments label="Filter by status" options={['All', ...STATUSES]} value={filter} onChange={setFilter} />
                    {shown.length === 0 ? <Empty>No incidents with this status.</Empty> : (
                        <ul className="rows">
                            {shown.map(i => (
                                <li className="row" key={i.id}>
                                    <div className="row__main">
                                        <span className="row__title">{i.title}</span>
                                        <span className="row__meta">
                                            {i.type} · {i.location} · {i.at} · reported by {i.by}
                                            {i.responders ? ' · police / EMS called' : ''}
                                        </span>
                                        {i.detail && <span className="row__meta">{i.detail}</span>}
                                    </div>
                                    <div className="row__end">
                                        <Chip tone={SEVERITY_TONE[i.severity]}>{i.severity}</Chip>
                                        <Chip tone={STATUS_TONE[i.status]}>{i.status}</Chip>
                                        {i.status !== 'Closed' && (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => advance(i)}>
                                                {i.status === 'Open' ? 'Start review' : 'Close'}
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default IncidentsScreen;
